// Absences : liste par mois, saisie, justification.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, IconButton, MenuItem, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip,
} from '@mui/material';
import { AddOutlined, ChevronLeft, ChevronRight, DeleteOutline, EditOutlined } from '@mui/icons-material';
import Layout from '../../components/Layout';
import AbsenceDialog from '../../components/rh/AbsenceDialog';
import { rhAPI } from '../../services/api';
import { apiError, dateFr } from '../../utils/format';
import { MOTIFS, periodLabel, periodRange, shiftRef, todayIso } from '../../utils/rh';

const RhAbsences = () => {
  const [ref, setRef] = useState(todayIso());
  const [employeId, setEmployeId] = useState('');
  const [rows, setRows] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const { from, to } = periodRange('mois', ref);

  useEffect(() => { rhAPI.employes().then((r) => setEmployes(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => {
    rhAPI.absences({ from, to, ...(employeId ? { employe_id: employeId } : {}) })
      .then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
  }, [from, to, employeId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'absence de ${a.prenom} ${a.nom} du ${dateFr(a.date_debut)} ?`)) return;
    try { await rhAPI.deleteAbsence(a.id); setToast('Absence supprimée'); load(); } catch (e) { setError(apiError(e)); }
  };

  return (
    <Layout title="Absences" plain
      actions={<Button variant="contained" startIcon={<AddOutlined />} onClick={() => setDialog({})}>Nouvelle absence</Button>}>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton aria-label="Mois précédent" onClick={() => setRef(shiftRef('mois', ref, -1))}><ChevronLeft /></IconButton>
        <Box sx={{ fontWeight: 600, minWidth: 140, textAlign: 'center' }}>{periodLabel('mois', ref)}</Box>
        <IconButton aria-label="Mois suivant" onClick={() => setRef(shiftRef('mois', ref, 1))}><ChevronRight /></IconButton>
        <TextField select size="small" label="Employé" value={employeId} sx={{ minWidth: 220, ml: 'auto' }} onChange={(e) => setEmployeId(e.target.value)}>
          <MenuItem value="">Tout le personnel</MenuItem>
          {employes.map((e) => <MenuItem key={e.id} value={e.id}>{e.nom} {e.prenom}</MenuItem>)}
        </TextField>
      </Box>
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employé</TableCell><TableCell>Du</TableCell><TableCell>Au</TableCell>
                <TableCell align="right">Jours ouvrés</TableCell><TableCell>Motif</TableCell><TableCell>Justificatif</TableCell>
                <TableCell>Commentaire</TableCell><TableCell>Saisi par</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell><b>{a.nom} {a.prenom}</b><br /><span style={{ fontSize: 12, color: '#5B6675' }}>{a.matricule}</span></TableCell>
                  <TableCell>{dateFr(a.date_debut)}</TableCell>
                  <TableCell>{dateFr(a.date_fin)}</TableCell>
                  <TableCell align="right">{a.nb_jours}</TableCell>
                  <TableCell>{MOTIFS[a.motif]}</TableCell>
                  <TableCell>
                    <Chip size="small" label={a.justifiee ? 'Justifiée' : 'Non justifiée'} color={a.justifiee ? 'info' : 'error'} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>{a.commentaire}</TableCell>
                  <TableCell>{a.saisi_par}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Modifier"><IconButton size="small" aria-label="Modifier l'absence" onClick={() => setDialog(a)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Supprimer"><IconButton size="small" aria-label="Supprimer l'absence" onClick={() => remove(a)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}>Aucune absence ce mois-ci</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <AbsenceDialog open={!!dialog} value={dialog} employes={employes} onClose={() => setDialog(null)}
        onSaved={(m) => { setDialog(null); setToast(m); load(); }} />
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default RhAbsences;
